import { useState, useRef, ReactNode } from 'react';

export type AccordionItem = {
    title: ReactNode;
    content: ReactNode;
};

type AccordionProps = {
    items: AccordionItem[];
    className?: string;
    titleClassName?: string;
    contentClassName?: string;
    /** 複数同時に開くことを許可する */
    allowMultiple?: boolean;
    /** allowMultiple時、初期状態で開くインデックスの配列 */
    defaultOpenIndices?: number[];
};

/**
 * アコーディオン
 * @param items アコーディオンの項目
 * @param className アコーディオンのクラス名
 * @param titleClassName タイトルのクラス名
 * @param contentClassName コンテンツのクラス名
 * @param allowMultiple 複数同時に開くことを許可する
 */
export function Accordion({
    items,
    className = '',
    titleClassName = '',
    contentClassName = '',
    allowMultiple = false,
    defaultOpenIndices = [],
}: AccordionProps) {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [openIndices, setOpenIndices] = useState<Set<number>>(
        () => new Set(allowMultiple ? defaultOpenIndices : []),
    );
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const isOpen = allowMultiple
        ? (index: number) => openIndices.has(index)
        : (index: number) => openIndex === index;

    const toggleAccordion = (index: number) => {
        const willOpen = allowMultiple ? !openIndices.has(index) : openIndex !== index;

        if (allowMultiple) {
            setOpenIndices((prev) => {
                const next = new Set(prev);
                if (next.has(index)) {
                    next.delete(index);
                } else {
                    next.add(index);
                }
                return next;
            });
        } else {
            setOpenIndex(openIndex === index ? null : index);
        }

        if (willOpen) {
            setTimeout(() => {
                const element = itemRefs.current[index];
                if (element) {
                    const headerHeight = 300;
                    const elementPosition =
                        element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - headerHeight;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                    });
                }
            }, 300);
        }
    };

    return (
        <div className={`w-full ${className}`}>
            {items.map((item, index) => (
                <div
                    key={index}
                    ref={(el) => {
                        itemRefs.current[index] = el;
                    }}
                >
                    {/* タイトル */}
                    <button
                        type="button"
                        className={`w-full flex justify-between items-center transition-colors gap-2 ${titleClassName}`}
                        onClick={() => toggleAccordion(index)}
                    >
                        <div className="flex-1 min-w-0 text-left">{item.title}</div>
                        {/* アイコン */}
                        <svg
                            className={`w-6 h-6 transform transition-transform duration-300 ${
                                isOpen(index) ? 'rotate-180' : ''
                            }`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="gray"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </button>
                    {/* コンテンツ */}
                    <div
                        className={`transition-all duration-300 ease-in-out ${
                            isOpen(index)
                                ? 'overflow-visible'
                                : 'max-h-0 overflow-hidden'
                        }`}
                    >
                        {typeof item.content === 'string' ? (
                            <div
                                className={`${contentClassName}`}
                                dangerouslySetInnerHTML={{ __html: item.content }}
                            />
                        ) : (
                            <div className={`${contentClassName}`}>{item.content}</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}
